import _, { isEqual } from "lodash";
import * as React from "react";
import { Stack } from "@mui/material";
import { CONTAINERS_NAME } from "../../utils/constants/common/componentName";
import { useXPage } from "../../hooks/useXPage";
import {
  TUseXElement,
  useXElement,
  useXElementUpdater,
} from "../../hooks/useXElement";
import { useXPath } from "../../hooks/useXPath";
import { useXCommonChildren } from "../../hooks/useXCommon";
import { XPageProps } from "../../utils/interfaces/core/XPage";
import { EDITABLE_PATHS } from "../../utils/constants/common/urlPaths";
import { TComponentType } from "../../utils/types/common/components.types";

const XStack = React.lazy(() => import("../containers/XStack"));
const XGrid = React.lazy(() => import("../containers/XGrid"));
const XCarousel = React.lazy(() => import("../containers/XCarousel"));
const XCategoryFilter = React.lazy(
  () => import("../containers/XCategoryFilter")
);
const XProductDetails = React.lazy(
  () => import("../containers/XProductDetails")
);
const XFooter = React.lazy(() => import("../common/XFooter"));
const XHeader = React.lazy(() => import("../common/XHeader"));

const XPage: React.FC<XPageProps> = ({
  initPage,
  path = "/",
  initFooter = {},
  initHeader = {},
  initCommon = [],

  children,
}) => {
  const [initiated, setInitiated] = React.useState(false);

  const { xPage, setXPage } = useXPage();

  const { xChildren: xHeader, setXChildren: setXHeader } =
    useXCommonChildren("HEADER");

  const { xChildren: xFooter, setXChildren: setXFooter } =
    useXCommonChildren("FOOTER");

  const { xPath, setXPath } = useXPath();

  React.useEffect(() => {
    // if the data has loaded we dont do not update anymore
    if (initiated) return;

    // SET THE CURRENT PATH
    if (path && !xPath) setXPath(path);

    // SET THE INITIAL DATA FOR THE CURRENT PAGE
    if (initPage) {
      EDITABLE_PATHS.forEach((path) => {
        if (initPage[path]) {
          setXPage({ path, elements: initPage[path] as TComponentType[] });
        }
      });
    }

    // if (initPage && xPath) {
    //   if (!!initPage[xPath]) {
    //     if ((initPage[xPath] as any[]).length > 0) {
    //       if (!xPage || (xPage as any).length === 0) {
    //         setXPage(initPage[xPath] ?? []);
    //       }
    //     }
    //   }
    // }

    // SET THE GENERAL HEADER DATA

    if (initCommon && initCommon.length > 0) {
      if (!xHeader || xHeader.length === 0) {
        const headers = initCommon.filter((c) => {
          if (!c.config) return false;

          return c.config.index.startsWith("HEADER");
        });

        if (headers.length > 0) setXHeader(headers);
      }

      if (!xFooter || xFooter.length === 0) {
        const footers = initCommon.filter((c) => {
          if (!c.config) return false;

          return c.config.index.startsWith("FOOTER");
        });

        if (footers.length > 0) setXFooter(footers);
      }
    }
    // if (initCommon && (!xHeader || xHeader.length === 0)) {
    //   setXHeader(
    //     initCommon.filter((c) => {
    //       if (!c.config) return false;

    //       return c.config.index.startsWith("HEADER");
    //     })
    //   );
    // }

    // SET THE GENERAL FOOTER DATA
    if (initCommon && (!xFooter || xFooter.length === 0)) {
      setXFooter(
        initCommon.filter((c) => {
          if (!c.config) return false;

          return c.config.index.startsWith("FOOTER");
        })
      );
    }

    setInitiated(true);
  }, [path, initPage, xPath, initCommon, initiated]);

  const content = (
    <React.Fragment>
      {
        // check page data
        // (xPage.length === 0
        //   ? [
        //       {
        //         name: "XCategoryFilter",
        //         config: { index: "ICI" },
        //         dataFetching: { limit: 4 },
        //         props: {
        //           all: {
        //             containerMode: "CENTER",
        //             columns: 4,
        //             gap: "15px",
        //             padding: { Top: "15px", bottom: "15px" },
        //           },
        //         },
        //       },
        //     ]
        //   : // SHOW PAGE
        //     (xPage as TComponentType[])
        // )

        [...xPage].map((child, i) => {
          return (
            <DynamicXElement
              key={i}
              child={child}
              index={child.config?.index ?? ""}
              position={i}
            />
          );
        })
      }
      {/* {XGUI && (
        <React.Suspense fallback={<p>loading editor...</p>}>
          <XGUI />
        </React.Suspense>
      )} */}
    </React.Fragment>
  );

  return (
    <Stack
      id="XPage"
      sx={{
        width: "100%",
        position: "relative",
        display: "block",
        // minHeight: "100vh",

        // '& [data-x-selected="true"]': {
        //   outline: `2px solid ${SECONDARY_COLOR}`,
        //   outlineOffset: `5px`,
        //   zIndex: 100,
        // },
        '& [data-x-hovered="true"]:not([data-x-selected="true"])': {
          outline: `3px solid #333`,
          outlineOffset: `5px`,
          zIndex: 100,
        },
      }}
    >
      <React.Suspense fallback={<p>loading header...</p>}>
        <XHeader initData={initHeader} />
      </React.Suspense>
      <React.Suspense fallback={<p>loading content...</p>}>
        {children ?? content}
      </React.Suspense>
      <React.Suspense fallback={<p>loading footer...</p>}>
        <XFooter initData={initFooter} />
      </React.Suspense>
    </Stack>
  );
};

export default XPage;

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const DynamicXElement: React.FC<{
  child: any;
  index: string;
  position: number;
}> = React.memo(({ child, index, position }) => {
  const { xElement, setXElement } = useXElement<any>(index);

  // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
  const setParent = useXElementUpdater<TUseXElement<any>>(
    child.config.childOf ?? "NO_PARENT"
  );

  React.useEffect(() => {
    setParent((old) => {
      const elementsIndexes = old.elementsIndexes
        ? [...old.elementsIndexes, index]
        : [index];

      return { ...old, elementsIndexes: _.uniq(elementsIndexes) };
    });

    const newEl: any = {
      name: child.name,

      index: child.index ?? index,

      hierachy: child.hierachy ?? "XPage-" + child.name,

      value: child.value ?? child.props ?? { all: {} },

      elementsIndexes: xElement
        ? xElement.elementsIndexes ?? []
        : child.elementsIndexes ?? [],

      hasChildren: child.hasChildren ?? CONTAINERS_NAME.includes(child.name),
    };

    if (child.childOf || child.config.childOf) {
      newEl.childOf = child.childOf ?? child.config.childOf;
    }

    if (child.dataFetching) newEl.dataFetching = child.dataFetching;

    setXElement(newEl);
  }, [index, position]);

  if (child.config.childOf) return null;

  switch (child.name) {
    case "XStack":
      return (
        <React.Suspense fallback={<p>loading XStack...</p>}>
          <XStack {...child} />
        </React.Suspense>
      );

    case "XGrid":
      return (
        <React.Suspense fallback={<p>loading XGrid...</p>}>
          <XGrid {...child} />
        </React.Suspense>
      );
    case "XCategoryFilter":
      return (
        <React.Suspense fallback={<p>loading XCategoryFilter...</p>}>
          <XCategoryFilter {...child} />
        </React.Suspense>
      );

    case "XProductDetails":
      return (
        <React.Suspense fallback={<p>loading XProductDetails...</p>}>
          <XProductDetails {...child} />
        </React.Suspense>
      );

    case "XCarousel":
      return (
        <React.Suspense fallback={<p>loading XCarousel...</p>}>
          <XCarousel {...child} />
        </React.Suspense>
      );

    default:
      return null;
  }
}, compareProps);
